import FetchClient, { type ApiResponse } from "./FetchClient";

export interface NotificationPreferences {
  dailyDealsNotifications?: boolean;
  crazyWootDeals?: boolean;
  marketingEmails?: boolean;
  onlineDealNotifications?: boolean;
}

export interface ExtraStore {
  storeId: string;
  distanceMiles: number;
  // The user's saved store this extra store is closest to.
  nearStoreId?: string;
}

export type HomeDepotExtraStore = ExtraStore;

export interface User {
  userId: string;
  discordUrl: string;
  discordUserId?: string;
  discordRefreshToken?: string;
  storeIds: string[];
  homeDepotStoreIds: string[];
  // Nearby stores the plan adds on top of homeDepotStoreIds (server-derived,
  // read-only): checked for fleet sale items, not fully crawled.
  homeDepotExtraStores?: HomeDepotExtraStore[];
  // Nearby Walmart stores the plan adds on top of storeIds (server-derived,
  // read-only): a light daily sweep of in-store clearance, rollback and
  // reduced-price items, not a full crawl.
  walmartExtraStores?: ExtraStore[];
  // Extra stores the member hid from the store picker, retailer -> store IDs.
  // Always present on servers that store it ({} when empty); missing on older
  // servers (see utils/hiddenExtraStores).
  hiddenExtraStores?: Partial<Record<string, string[]>>;
  accountType: "free" | "basic" | "starter" | "pro" | "enterprise";
  categories: string[];
  homeDepotCategories: string[];
  dollarGeneralStoreIds: string[];
  dollarGeneralCategories: string[];
  cvsStoreIds?: string[];
  bestbuyStoreIds?: string[];
  lowesStoreIds?: string[];
  tractorSupplyStoreIds?: string[];
  northernToolStoreIds?: string[];
  samsClubStoreIds?: string[];
  aceHardwareStoreIds?: string[];
  costcoStoreIds?: string[];
  searchTerms?: string[];
  running: boolean;
  name: string;
  verified: boolean;
  setUp: boolean;
  freeTierEnabled?: boolean;
  customerId: string;
  admin?: boolean;
  /** Admin-managed access to the experimental Home Depot penny candidate feed. */
  pennyBeta?: boolean;
  allowSendToDiscord?: boolean;
  allowAddLead?: boolean;
  /** When true, bot-filters UI shows/edits all saved stores even if above plan max */
  allowMoreStores?: boolean;
  unlimitedStoreChanges?: boolean;
  /** Admin-set override raising the user's max price alerts above the plan default */
  maxAlertsOverride?: number;
  /** Admin-set Walmart stock-check radius (miles) and closest-store count; 0/undefined = global default */
  stockCheckRadiusOverride?: number;
  stockCheckStoreLimitOverride?: number;
  /** Admin-set Walmart search provider pin; blank/undefined uses the global Dynamo config */
  walmartProviderOverride?: "tempo" | "walmartapp" | "zephyr" | "";
  godModeUserId?: string;
  godModeOriginalUserId?: string;
  hadTrial?: boolean;
  created?: number; // ISO date string
  lastActive?: number; // Unix timestamp in milliseconds
  lastStoreChange?: number; // Unix timestamp in milliseconds
  lastHomeDepotStoreChange?: number; // Unix timestamp in milliseconds
  lastDollarGeneralStoreChange?: number; // Unix timestamp in milliseconds
  lastTargetStoreChange?: number;
  lastBestBuyStoreChange?: number;
  lastCostcoStoreChange?: number;
  recentStoreChanges?: Record<string, number[]>;
  lastLowesStoreChange?: number;
  lastTractorSupplyStoreChange?: number;
  lastNorthernToolStoreChange?: number;
  lastSamsClubStoreChange?: number;
  lastAceHardwareStoreChange?: number;
  retailers?: string[]; // Array of retailer names like ["walmart", "home_depot"]
  /** Admin-set per-retailer exceptions. Missing keys inherit the user's plan. */
  retailerAccessOverrides?: Record<string, boolean>;
  subscriptionSrc?: string; // "stripe" | "revenuecat_app_store" | ... (who owns the active plan)
  targetStoreIds?: string[];
  targetCategories?: string[];
  zipCode?: string;
  pennyZipCode?: string;
  /** IANA timezone used for local calendar-day store-change limits. */
  timeZone?: string;
  /** App language last reported by a client; picks the email language. */
  language?: "en" | "es";
  dealIntent?: "online" | "instore";
  /** ZIP the account asked to hear about when onboarding found no stores near it (server-stamped coverageRequestedAt). */
  coverageRequestZip?: string;
  coverageRequestEmail?: string;
  coverageRequestedAt?: number;
  banned?: boolean;
  lastSeenAnnouncementAt?: number;
  notificationPreferences?: NotificationPreferences;
  tourSeen?: boolean;
  communityGuidelinesAccepted?: boolean;
  communityAdultConfirmed?: boolean;
  avatarUrl?: string;
  retentionDiscountsUsed?: number;
  introOfferDiscountsUsed?: number;
  /** Unix ms the Stripe subscription auto-resumes at. 0/absent = not paused. */
  subscriptionPausedUntil?: number;
  /** Unix ms the paid period ends for a scheduled pause; full access until then. 0/absent = pause already started. */
  subscriptionPauseAccessUntil?: number;
	referralMonthsBanked?: number;
	referralMonthsRedeemed?: number;
	referralRewardsYear?: number;
	referralRewardsEarnedThisYear?: number;
	referralInviteDismissed?: boolean;
  /** Admin-granted one-time 50% off first month, applied at the next Stripe checkout */
  next50OffCouponPending?: boolean;
  referralId?: string;
  signupReferrerDomain?: string;
  toltCustomerId?: string;
  toltClickId?: string;
}

export interface AdminReferralPartner {
  ref: string;
  partnerId: string;
  linkId: string;
  name: string;
  email: string;
  company?: string;
  status?: string;
}

export interface AdminReferralStatus {
  referralId?: string;
  toltCustomerId?: string;
  toltClickId?: string;
  partner?: AdminReferralPartner;
}

export function resolveNotificationPreferences(user: Pick<User, "notificationPreferences"> | null | undefined) {
  return {
    dailyDealsNotifications: user?.notificationPreferences?.dailyDealsNotifications ?? true,
    crazyWootDeals: user?.notificationPreferences?.crazyWootDeals ?? true,
    marketingEmails: user?.notificationPreferences?.marketingEmails ?? false,
    onlineDealNotifications: user?.notificationPreferences?.onlineDealNotifications ?? true,
  };
}

/** Trimmed user shape returned by GET /admin/users for the admin table. */
export interface AdminUserSummary {
  userId: string;
  name?: string;
  discordUserId?: string;
  created?: number;
  banned?: boolean;
  retailers?: string[];
  storeIds?: string[];
  homeDepotStoreIds?: string[];
  targetStoreIds?: string[];
  dollarGeneralStoreIds?: string[];
  bestbuyStoreIds?: string[];
  categories?: string[];
  referralId?: string;
  signupReferrerDomain?: string;
}

export async function getUsers(): Promise<AdminUserSummary[]> {
  const response = await FetchClient.get<ApiResponse<AdminUserSummary[]>>("/admin/users");
  return response.data;
}

export async function getAdminUser(userId: string): Promise<User> {
  const params = new URLSearchParams({ userId });
  const response = await FetchClient.get<ApiResponse<User>>(
    `/admin/user?${params.toString()}`,
  );
  return response.data;
}

export async function getUser(): Promise<User> {
  const response = await FetchClient.get<ApiResponse<User>>("/user");
  return response.data;
}

export async function createUser(
  userData: Omit<User, "userId">,
): Promise<User> {
  const response = await FetchClient.post<ApiResponse<User>>(
    "/admin/users",
    userData,
  );
  return response.data;
}

export async function patchUser(data: Partial<User>): Promise<User> {
  const includesStoreChange = [
    "storeIds",
    "homeDepotStoreIds",
    "targetStoreIds",
    "dollarGeneralStoreIds",
    "bestbuyStoreIds",
    "costcoStoreIds",
    "lowesStoreIds",
    "tractorSupplyStoreIds",
    "northernToolStoreIds",
    "samsClubStoreIds",
    "aceHardwareStoreIds",
  ].some((field) => Object.prototype.hasOwnProperty.call(data, field));
  let body = data;
  if (includesStoreChange) {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timeZone) body = { ...data, timeZone };
    } catch {
      // Older runtimes may not expose an IANA timezone; the server uses the account fallback.
    }
  }
  const response = await FetchClient.patch<ApiResponse<User>>("/user", {
    body,
  });
  return response.data;
}

// Turns marketing email on from an in-app prompt; the server records the
// consent source and time.
export async function optInMarketingEmails(source: "in-app-card" | "settings-toggle"): Promise<User> {
  const response = await FetchClient.post<ApiResponse<User>>("/email/opt-in", { source });
  return response.data;
}

export async function uploadAvatar(file: File): Promise<User> {
  const form = new FormData();
  form.append("photo", file);
  const response = await FetchClient.postForm<ApiResponse<User>>("/user/avatar", form);
  return response.data;
}

export async function deleteAvatar(): Promise<User> {
  const response = await FetchClient.delete<ApiResponse<User>>("/user/avatar");
  return response.data;
}

export async function resetAdminUserStoreChangeLimits(userId: string): Promise<User> {
  const response = await FetchClient.patch<ApiResponse<{ user: User; resetRetailers?: string[] }>>(
    `/admin/users/${userId}`,
    { body: { resetStoreChangeLimits: true } }
  );
  return response.data.user ?? (response.data as unknown as User);
}

export async function resetAdminUserStoreChangeLimit(
  userId: string,
  retailer: string,
): Promise<User> {
  const response = await FetchClient.patch<ApiResponse<{ user: User; resetRetailers: string[] }>>(
    `/admin/users/${encodeURIComponent(userId)}`,
    { body: { resetStoreChangeLimit: retailer } },
  );
  return response.data.user;
}

export async function patchAdminUser(
  userId: string,
  data: Partial<User>,
): Promise<User> {
  const response = await FetchClient.patch<ApiResponse<User>>(
    `/admin/users/${userId}`,
    {
      body: data,
    },
  );
  return response.data;
}

export function verifyEmail(code: string): Promise<ApiResponse<void>> {
  return FetchClient.post<ApiResponse<void>>("/user/verify", { code });
}

export function resendVerificationCode(): Promise<
  ApiResponse<{ message: string; verified: boolean; attemptsLeft?: number }>
> {
  return FetchClient.post<
    ApiResponse<{ message: string; verified: boolean; attemptsLeft?: number }>
  >("/user/resend-verification", {});
}

export async function finishOnboarding(
  categories: string[],
  storeIds: string[],
  homeDepotCategories: string[],
  homeDepotStoreIds: string[],
): Promise<ApiResponse<void>> {
  const response = await FetchClient.post<ApiResponse<void>>(
    "/user/onboarding",
    {
      categories,
      storeIds,
      homeDepotCategories,
      homeDepotStoreIds,
    },
  );

  return response;
}

// God Mode functions
export async function activateGodMode(targetUserId: string): Promise<User> {
  const response = await FetchClient.post<ApiResponse<User>>(
    "/admin/god-mode/activate",
    {
      targetUserId,
    },
  );
  return response.data;
}

export async function deactivateGodMode(): Promise<User> {
  const response = await FetchClient.post<ApiResponse<User>>(
    "/admin/god-mode/deactivate",
    {},
  );
  return response.data;
}

// Kill user function (admin only)
export async function killUser(
  userId: string,
): Promise<ApiResponse<{ message: string; userId: string }>> {
  const response = await FetchClient.post<
    ApiResponse<{ message: string; userId: string }>
  >(`/admin/users/${userId}/kill`, {});
  return response;
}

// Utility function to get user's retailers with default fallback
export function getUserRetailers(user: User | null): string[] {
  if (!user || !user.retailers || user.retailers.length === 0) {
    return ["walmart"];
  }
  // CVS is paused in the clients. Ignore exact or plan-qualified CVS entries
  // so an existing account cannot be redirected into the dormant pilot.
  const visibleRetailers = user.retailers.filter(
    (retailer) => !/^cvs(?:_|$)/i.test(retailer.trim())
  );
  return visibleRetailers.length > 0 ? visibleRetailers : ["walmart"];
}

// Admin retailer management functions
export async function getStripeRetailers(userId: string): Promise<string[]> {
  const response = await FetchClient.get<ApiResponse<{ retailers: string[] }>>(
    `/admin/users/${userId}/stripe-retailers`,
  );
  return response.data.retailers;
}

export async function syncRetailersFromStripe(userId: string): Promise<User> {
  const response = await FetchClient.post<ApiResponse<User>>(
    `/admin/users/${userId}/sync-retailers`,
    {},
  );
  return response.data;
}

export async function addRetailer(
  userId: string,
  retailer: string,
): Promise<User> {
  const response = await FetchClient.post<ApiResponse<User>>(
    `/admin/users/${userId}/retailers`,
    {
      retailer,
    },
  );
  return response.data;
}

export async function removeRetailer(
  userId: string,
  retailer: string,
): Promise<User> {
  const response = await FetchClient.delete<ApiResponse<User>>(
    `/admin/users/${userId}/retailers/${encodeURIComponent(retailer)}`,
  );
  return response.data;
}

export async function getAdminReferralPartners(): Promise<AdminReferralPartner[]> {
  const response = await FetchClient.get<ApiResponse<AdminReferralPartner[]>>(
    "/admin/referral-partners",
  );
  return response.data;
}

export async function getAdminUserReferral(userId: string): Promise<AdminReferralStatus> {
  const response = await FetchClient.get<ApiResponse<AdminReferralStatus>>(
    `/admin/users/${encodeURIComponent(userId)}/referral`,
  );
  return response.data;
}

export async function setAdminUserReferral(
  userId: string,
  referralId: string,
): Promise<{ user: User; referral: AdminReferralStatus }> {
  const response = await FetchClient.put<
    ApiResponse<{ user: User; referral: AdminReferralStatus }>
  >(`/admin/users/${encodeURIComponent(userId)}/referral`, { referralId });
  return response.data;
}

export async function deleteAccount(): Promise<
  ApiResponse<{ message: string }>
> {
  const response = await FetchClient.delete<ApiResponse<{ message: string }>>(
    "/user/delete-account",
  );
  return response;
}
